import { useState, useCallback } from 'react';
import { ToastMessage } from '../utils/types';

interface ToastContextValue {
  toasts: ToastMessage[];
  addToast: (message: string, type?: ToastMessage['type'], duration?: number) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

let toastId = 0;
const generateToastId = () => `toast-${++toastId}`;

// 全局Toast状态管理
class ToastManager {
  private listeners: Set<(toasts: ToastMessage[]) => void> = new Set();
  private toasts: ToastMessage[] = [];

  subscribe(listener: (toasts: ToastMessage[]) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getToasts() {
    return this.toasts;
  }

  addToast(message: string, type: ToastMessage['type'] = 'info', duration = 3000) {
    const toast: ToastMessage = {
      id: generateToastId(),
      message,
      type,
      duration,
    };

    this.toasts = [...this.toasts, toast];
    this.notify();

    // 自动移除Toast
    if (duration > 0) {
      setTimeout(() => {
        this.removeToast(toast.id);
      }, duration);
    }

    return toast.id;
  }

  removeToast(id: string) {
    this.toasts = this.toasts.filter(toast => toast.id !== id);
    this.notify();
  }

  clearToasts() {
    this.toasts = [];
    this.notify();
  }

  private notify() {
    this.listeners.forEach(listener => listener(this.toasts));
  }
}

const toastManager = new ToastManager();

// Hook for using toast functionality
export const useToast = (): ToastContextValue => {
  const [toasts, setToasts] = useState<ToastMessage[]>(toastManager.getToasts());

  // 订阅Toast状态变化
  useState(() => {
    const unsubscribe = toastManager.subscribe(setToasts);
    return unsubscribe;
  });

  const addToast = useCallback((message: string, type: ToastMessage['type'] = 'info', duration = 3000) => {
    return toastManager.addToast(message, type, duration);
  }, []);

  const removeToast = useCallback((id: string) => {
    toastManager.removeToast(id);
  }, []);

  const clearToasts = useCallback(() => {
    toastManager.clearToasts();
  }, []);

  return {
    toasts,
    addToast,
    removeToast,
    clearToasts,
  };
};

// 便捷方法
export const toast = {
  success: (message: string, duration?: number) => toastManager.addToast(message, 'success', duration),
  error: (message: string, duration?: number) => toastManager.addToast(message, 'error', duration),
  warning: (message: string, duration?: number) => toastManager.addToast(message, 'warning', duration),
  info: (message: string, duration?: number) => toastManager.addToast(message, 'info', duration),
};
